import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { TFunction } from 'i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Save } from 'lucide-react';
import { toast } from 'sonner';

import {
  apiAdminshopsIdPatch,
  getApiShopsGetCollectionQueryKey,
  getApiShopsIdGetQueryKey
} from '@r/api/generated/client.ts';
import {
  apiAdminshopsIdPatchBody,
  apiAdminshopsIdPatchBodyDescriptionMax,
  apiAdminshopsIdPatchBodyDisplayNameMax
} from '@r/api/generated-zod/index.ts';
import type {
  ShopShopReadModel,
  ShopShopUpdateJsonMergePatchModel,
  ShopWorkspaceShopReadModel
} from '@r/api/generated/client.schemas.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Textarea } from '@ui/textarea.tsx';
import { Button } from '@ui/button.tsx';
import { Separator } from '@ui/separator.tsx';

/**
 * Schéma dérivé de {@link apiAdminshopsIdPatchBody} : le PATCH OpenAPI laisse les champs
 * optionnels ; ici nom et description restent obligatoires à l’édition. Les contacts
 * réutilisent le Zod généré (nullish) — valeurs vides = `null` dans le formulaire.
 */
function buildRewardShopUpdateSchema(t: TFunction) {
  const { shape } = apiAdminshopsIdPatchBody;

  return z.object({
    displayName: shape.displayName.unwrap().min(1, t('admin.shops.form.display.name.required')),
    description: shape.description.unwrap().min(1, t('admin.shops.form.description.required')),
    operationalContact: shape.operationalContact,
    technicalContact: shape.technicalContact
  });
}

type RewardShopUpdateValues = z.infer<ReturnType<typeof buildRewardShopUpdateSchema>>;

type UpdateRewardShopFormProps = Omit<ShopShopReadModel, 'shopWorkspace'> & {
  shopWorkspace: ShopWorkspaceShopReadModel;
  onSuccess?: () => void;
};

function buildTechnicalName(displayName: string): string {
  const slug = displayName
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

  return (slug || displayName.trim()).slice(0, 100);
}

/** Édition limitée de la boutique reward (programme à paliers). */
export function UpdateRewardShopForm(shop: UpdateRewardShopFormProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const showOrvalErrorToast = useOrvalErrorToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formSchema = useMemo(() => buildRewardShopUpdateSchema(t), [t]);

  const form = useForm<RewardShopUpdateValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      displayName: shop.displayName,
      description: shop.description,
      operationalContact: shop.operationalContact ?? null,
      technicalContact: shop.technicalContact ?? null
    },
    mode: 'onChange'
  });

  const onSubmit = async (values: RewardShopUpdateValues) => {
    setIsSubmitting(true);

    try {
      const dirtyFields = form.formState.dirtyFields;

      const shopPayload: ShopShopUpdateJsonMergePatchModel = {
        ...(dirtyFields.displayName
          ? {
              name: buildTechnicalName(values.displayName),
              displayName: values.displayName.trim()
            }
          : {}),
        ...(dirtyFields.description ? { description: values.description.trim() } : {}),
        ...(dirtyFields.operationalContact
          ? {
              operationalContact: values.operationalContact?.trim()
                ? values.operationalContact.trim()
                : null
            }
          : {}),
        ...(dirtyFields.technicalContact
          ? {
              technicalContact: values.technicalContact?.trim()
                ? values.technicalContact.trim()
                : null
            }
          : {})
      };

      if (Object.keys(shopPayload).length === 0) return;

      await apiAdminshopsIdPatch(String(shop.id), shopPayload);

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getApiShopsIdGetQueryKey(String(shop.id)) }),
        queryClient.invalidateQueries({ queryKey: getApiShopsGetCollectionQueryKey() })
      ]);

      toast.success(t('form.shop.success', { shopName: values.displayName }));
      form.reset(values);
      shop.onSuccess?.();
    } catch (error) {
      showOrvalErrorToast(error, t('form.shop.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form} schema={formSchema}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
        <section className="space-y-6">
          <h3 className="text-sm font-medium text-muted-foreground">
            {t('admin.shops.update.form.section.personalization')}
          </h3>
          <Separator />

          <FormField
            control={form.control}
            name="displayName"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('admin.shops.update.form.display.name.label')}</FormLabel>
                <FormControl>
                  <Input
                    maxLength={apiAdminshopsIdPatchBodyDisplayNameMax}
                    error={!!fieldState.error}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="description"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('admin.shops.update.form.description.label')}</FormLabel>
                <FormControl>
                  <Textarea
                    rows={3}
                    maxLength={apiAdminshopsIdPatchBodyDescriptionMax}
                    error={!!fieldState.error}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </section>

        <section className="space-y-6">
          <h3 className="text-sm font-medium text-muted-foreground">
            {t('admin.shops.update.form.section.contact')}
          </h3>
          <Separator />

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <FormField
              control={form.control}
              name="operationalContact"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>{t('admin.shops.update.form.operational.contact.label')}</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder={t('admin.shops.form.email.placeholder')}
                      error={!!fieldState.error}
                      value={field.value ?? ''}
                      onChange={(event) => {
                        const next = event.target.value;
                        field.onChange(next === '' ? null : next);
                      }}
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="technicalContact"
              render={({ field, fieldState }) => (
                <FormItem>
                  <FormLabel>{t('admin.shops.update.form.technical.contact.label')}</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder={t('admin.shops.form.email.placeholder')}
                      error={!!fieldState.error}
                      value={field.value ?? ''}
                      onChange={(event) => {
                        const next = event.target.value;
                        field.onChange(next === '' ? null : next);
                      }}
                      onBlur={field.onBlur}
                      name={field.name}
                      ref={field.ref}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </section>

        <Button loading={isSubmitting} className="w-full" type="submit">
          <Save className="mr-2 size-4" />
          {t('form.save.modifications')}
        </Button>
      </form>
    </Form>
  );
}
